import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ToastContext } from './toast-context';
let counter = 0;
function nextId() {
    counter += 1;
    return `t_${Date.now().toString(36)}_${counter}`;
}
/**
 * Stellt `useToast()` bereit. Toasts werden zentral gehalten und nach Ablauf
 * automatisch entfernt — App-lokale Toast-Implementierungen werden so abgeloest.
 * `pause()`/`resume()` halten die Auto-Dismiss-Timer an, solange der Zeiger
 * oder der Fokus auf dem Toaster liegt (WCAG 2.2.1: zeitliche Grenzen).
 */
export function ToastProvider({ children, defaultDuration = 5000 }) {
    const [toasts, setToasts] = useState([]);
    const timers = useRef(new Map());
    const paused = useRef(false);
    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        const pending = timers.current.get(id);
        if (pending) {
            if (pending.timer)
                clearTimeout(pending.timer);
            timers.current.delete(id);
        }
    }, []);
    const schedule = useCallback((id, ms) => {
        const timer = setTimeout(() => dismiss(id), ms);
        timers.current.set(id, { timer, remaining: ms, startedAt: Date.now() });
    }, [dismiss]);
    const push = useCallback((input) => {
        const id = nextId();
        const duration = input.duration ?? defaultDuration;
        setToasts((prev) => [...prev, { id, message: input.message, severity: input.severity ?? 'info', duration }]);
        if (duration > 0) {
            if (paused.current) {
                timers.current.set(id, { timer: null, remaining: duration, startedAt: Date.now() });
            }
            else {
                schedule(id, duration);
            }
        }
        return id;
    }, [defaultDuration, schedule]);
    const pause = useCallback(() => {
        if (paused.current)
            return;
        paused.current = true;
        const now = Date.now();
        timers.current.forEach((pending) => {
            if (!pending.timer)
                return;
            clearTimeout(pending.timer);
            pending.remaining = Math.max(0, pending.remaining - (now - pending.startedAt));
            pending.timer = null;
        });
    }, []);
    const resume = useCallback(() => {
        if (!paused.current)
            return;
        paused.current = false;
        timers.current.forEach((pending, id) => {
            if (pending.timer)
                return;
            // Mindestens eine kurze Restzeit, damit der Nutzer die Hand wegnehmen kann.
            schedule(id, Math.max(pending.remaining, 1000));
        });
    }, [schedule]);
    useEffect(() => {
        const map = timers.current;
        return () => {
            map.forEach((p) => {
                if (p.timer)
                    clearTimeout(p.timer);
            });
            map.clear();
        };
    }, []);
    const store = useMemo(() => ({ toasts, push, dismiss, pause, resume }), [toasts, push, dismiss, pause, resume]);
    return _jsx(ToastContext.Provider, { value: store, children: children });
}
